import { z } from 'zod'

import { EnumBuilder, type ZodInfer } from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import { NoteSchema, TimestampSchema } from './property.models.ts'
import { ReviewRoundNumberSchema } from './review.models.ts'
import {
  ReviewIdSchema,
  RoundIdSchema,
  RoundUrnSchema,
  UserIdSchema,
} from './unique-id.models.ts'

/**
 * RoundStatus
 */
export const [RoundStatuses, RoundStatusSchema, RoundStatusEnum] = EnumBuilder(
  'in_progress',
  'changes_requested',
  'approved'
)
export type RoundStatus = ZodInfer<typeof RoundStatusSchema>

/**
 * Round Submission
 */
const RoundSubmissionSchema = z.object({
  actorId: UserIdSchema,
  timestamp: TimestampSchema,
})

/**
 * Round Proposal
 */

export const RoundProposalSchema = z
  .object({
    notes: NoteSchema,
    submitted: RoundSubmissionSchema,
  })
  .partial()

export type RoundProposal = ZodInfer<typeof RoundProposalSchema>

/**
 * Round Feedback
 */

export const RoundFeedbackSchema = z
  .object({
    notes: NoteSchema,
    submitted: RoundSubmissionSchema,
  })
  .partial()

export type RoundFeedback = ZodInfer<typeof RoundFeedbackSchema>

export const RoundDomainSelectModelSchema = z.object({
  roundId: RoundIdSchema,
  roundUrn: RoundUrnSchema,
  reviewId: ReviewIdSchema,
  roundNumber: ReviewRoundNumberSchema,
  status: RoundStatusSchema,
  proposal: RoundProposalSchema,
  feedback: RoundFeedbackSchema,
})

export type RoundDomainSelectModel = ZodInfer<
  typeof RoundDomainSelectModelSchema
>

/**
 * RoundDomainInsertModel
 */
export const RoundDomainInsertModelSchema = RoundDomainSelectModelSchema.omit({
  roundUrn: true,
  status: true, // Always inserted as in_progress
  proposal: true,
  feedback: true,
}).partial({
  roundId: true,
})

export type RoundDomainInsertModel = ZodInfer<
  typeof RoundDomainInsertModelSchema
>

/**
 * RoundDomainUpdateModel
 */
export const RoundDomainUpdateModelSchema = RoundDomainSelectModelSchema.pick({
  status: true,
})
  .extend({
    proposal: RoundProposalSchema.extend(
      z.object({ notes: NoteSchema.nullable() }).partial().shape
    ),
    feedback: RoundFeedbackSchema.extend(
      z.object({ notes: NoteSchema.nullable() }).partial().shape
    ),
  })
  .partial()

export type RoundDomainUpdateModel = ZodInfer<
  typeof RoundDomainUpdateModelSchema
>

/**
 * RoundDomainSelectModelFilter
 */
export const RoundDomainSelectModelFiltersSchema = z
  .object({
    roundId: ModelFilterSchema(RoundIdSchema),
    roundUrn: ModelFilterSchema(RoundUrnSchema),
    reviewId: ModelFilterSchema(ReviewIdSchema),
    roundNumber: ModelFilterSchema(ReviewRoundNumberSchema),
    status: ModelFilterSchema(RoundStatusSchema),
  })
  .partial()

export type RoundDomainSelectModelFilters = ZodInfer<
  typeof RoundDomainSelectModelFiltersSchema
>
